import React from "react";
import { colors, radius, spacing, typography } from "./tokens";
import Button from "./Button";

const containerBaseStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  overflow: "hidden",
  background: colors.surface,
};

const tableBaseStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const headerRowStyle = {
  background: colors.tableHeader,
};

const headerCellBaseStyle = {
  padding: `${spacing.sm}px ${spacing.lg}px`,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  color: colors.textSecondary,
  textAlign: "left",
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
};

const headerButtonStyle = {
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  boxShadow: "none",
};

const cellBaseStyle = {
  padding: `${spacing.sm}px ${spacing.lg}px`,
  fontSize: typography.size.sm,
  color: colors.text,
  borderTop: `1px solid ${colors.border}`,
  verticalAlign: "top",
};

const emptyStateStyle = {
  padding: `${spacing.lg}px ${spacing.lg}px`,
  color: colors.secondary,
  fontSize: typography.size.sm,
  textAlign: "center",
};

function Table({
  columns = [],
  data = [],
  sortBy,
  sortDirection = "asc",
  onSort,
  draggableColumns = false,
  onColumnReorder,
  resizableColumns = false,
  columnWidths = {},
  onColumnResize,
  emptyState = "No data available.",
  caption,
  getRowKey,
  onRowClick,
  getRowProps,
  style,
  tableStyle,
}) {
  const [draggedColumnId, setDraggedColumnId] = React.useState(null);
  const [dragOverColumnId, setDragOverColumnId] = React.useState(null);
  const [hoveredRowKey, setHoveredRowKey] = React.useState(null);
  const resizeStateRef = React.useRef(null);
  const hasRows = Array.isArray(data) && data.length > 0;
  const columnCount = Math.max(columns.length, 1);

  const resolveColumnId = (column) => column.id || column.accessor;
  const resolveColumnWidth = (column) => {
    const columnId = resolveColumnId(column);
    const explicitWidth = columnId ? columnWidths?.[columnId] : undefined;
    if (typeof explicitWidth === "number") return explicitWidth;
    if (typeof explicitWidth === "string") {
      const parsed = parseInt(explicitWidth, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    if (typeof column.width === "number") return column.width;
    if (typeof column.width === "string") {
      const parsed = parseInt(column.width, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
  };

  const renderCell = (column, row, rowIndex) => {
    if (typeof column.render === "function") {
      return column.render(row, rowIndex);
    }

    if (typeof column.accessor === "function") {
      return column.accessor(row, rowIndex);
    }

    if (typeof column.accessor === "string") {
      const value = row?.[column.accessor];
      return value ?? "-";
    }

    return "-";
  };

  const handleSort = (column) => {
    if (!column.sortable || typeof onSort !== "function") return;
    const columnId = resolveColumnId(column);
    if (!columnId) return;
    const nextDirection =
      sortBy === columnId && sortDirection === "asc" ? "desc" : "asc";
    onSort(columnId, nextDirection, column);
  };

  const handleColumnDrop = (targetColumnId) => {
    if (!draggableColumns || typeof onColumnReorder !== "function") return;
    if (!draggedColumnId || !targetColumnId || draggedColumnId === targetColumnId) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }
    onColumnReorder(draggedColumnId, targetColumnId);
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  };

  React.useEffect(() => {
    if (!resizableColumns || typeof onColumnResize !== "function") return undefined;

    const handlePointerMove = (event) => {
      const activeResize = resizeStateRef.current;
      if (!activeResize?.columnId) return;
      const nextWidth = Math.max(
        activeResize.minWidth,
        activeResize.startWidth + (event.clientX - activeResize.startX)
      );
      onColumnResize(activeResize.columnId, nextWidth);
    };

    const handlePointerUp = () => {
      resizeStateRef.current = null;
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [onColumnResize, resizableColumns]);

  return (
    <div
      style={{
        ...containerBaseStyle,
        ...(resizableColumns ? { overflowX: "auto", overflowY: "hidden" } : null),
        ...style,
      }}
    >
      <table
        style={{
          ...tableBaseStyle,
          ...(resizableColumns
            ? {
                width: "max-content",
                minWidth: "100%",
                tableLayout: "fixed",
              }
            : null),
          ...tableStyle,
        }}
      >
        {caption && <caption style={{ padding: spacing.sm }}>{caption}</caption>}
        {resizableColumns && (
          <colgroup>
            {columns.map((column) => {
              const columnId = resolveColumnId(column);
              const resolvedWidth = resolveColumnWidth(column);
              return (
                <col
                  key={`col-${columnId || "column"}`}
                  style={{
                    width: resolvedWidth,
                    minWidth: column.minWidth,
                  }}
                />
              );
            })}
          </colgroup>
        )}
        <thead style={headerRowStyle}>
          <tr>
            {columns.map((column) => {
              const columnId = resolveColumnId(column);
              const isSorted = columnId && sortBy === columnId;
              const ariaSort = isSorted
                ? sortDirection === "desc"
                  ? "descending"
                  : "ascending"
                : "none";
              const align = column.align || "left";
              const headerContent = column.header ?? columnId ?? "";
              const labelText =
                typeof headerContent === "string" ? headerContent : columnId || "column";
              const showSortIndicator = isSorted ? (sortDirection === "desc" ? "DESC" : "ASC") : null;

              return (
                <th
                  key={columnId || labelText}
                  scope="col"
                  aria-sort={ariaSort}
                  onDragOver={
                    draggableColumns && columnId
                      ? (event) => {
                          event.preventDefault();
                          if (dragOverColumnId !== columnId) {
                            setDragOverColumnId(columnId);
                          }
                        }
                      : undefined
                  }
                  onDrop={draggableColumns && columnId ? () => handleColumnDrop(columnId) : undefined}
                  onDragLeave={
                    draggableColumns && columnId
                      ? () => {
                          if (dragOverColumnId === columnId) {
                            setDragOverColumnId(null);
                          }
                        }
                      : undefined
                  }
                  style={{
                    ...headerCellBaseStyle,
                    textAlign: align,
                    width: resolveColumnWidth(column),
                    minWidth: column.minWidth,
                    position: "relative",
                    ...(draggableColumns && dragOverColumnId === columnId
                      ? { background: colors.accentSubtle }
                      : null),
                    ...column.headerStyle,
                  }}
                >
                  <div
                    draggable={Boolean(draggableColumns && columnId)}
                    onDragStart={
                      draggableColumns && columnId
                        ? (event) => {
                            setDraggedColumnId(columnId);
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData("text/plain", String(columnId));
                          }
                        : undefined
                    }
                    onDragEnd={
                      draggableColumns
                        ? () => {
                            setDraggedColumnId(null);
                            setDragOverColumnId(null);
                          }
                        : undefined
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacing.xs,
                      cursor: draggableColumns && columnId ? "grab" : "default",
                      opacity: draggedColumnId === columnId ? 0.6 : 1,
                    }}
                    aria-label={draggableColumns && columnId ? `Drag column ${labelText}` : undefined}
                  >
                    {column.sortable && typeof onSort === "function" ? (
                      <Button
                        type="button"
                        onClick={() => handleSort(column)}
                        aria-label={`Sort by ${labelText}`}
                        variant="ghost"
                        style={headerButtonStyle}
                      >
                        <span>{headerContent}</span>
                        {showSortIndicator && <span>{showSortIndicator}</span>}
                      </Button>
                    ) : (
                      <span>{headerContent}</span>
                    )}
                    {draggableColumns && columnId && <span style={{ color: colors.textMuted, fontSize: typography.size.xs }}>::</span>}
                  </div>
                  {resizableColumns && columnId && typeof onColumnResize === "function" && (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${labelText}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const widthFromMap = columnWidths?.[columnId];
                        const resolvedWidth =
                          typeof widthFromMap === "number"
                            ? widthFromMap
                            : Number.isFinite(resolveColumnWidth(column))
                              ? resolveColumnWidth(column)
                              : event.currentTarget.parentElement?.offsetWidth || 160;
                        resizeStateRef.current = {
                          columnId,
                          startX: event.clientX,
                          startWidth: resolvedWidth,
                          minWidth: column.minWidth || 80,
                        };
                      }}
                      style={resizeHandleStyle}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            data.map((row, rowIndex) => {
              const rowKey = getRowKey ? getRowKey(row, rowIndex) : row?.id ?? row?.key ?? rowIndex;
              const clickable = typeof onRowClick === "function";
              const rowProps = typeof getRowProps === "function" ? getRowProps(row, rowIndex) : {};
              const handleActivate = (event) => {
                if (typeof rowProps.onClick === "function") {
                  rowProps.onClick(event);
                }
                if (clickable) onRowClick(row, rowIndex);
              };
              const handleKeyDown = (event) => {
                if (!clickable) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleActivate();
                }
                if (typeof rowProps.onKeyDown === "function") {
                  rowProps.onKeyDown(event);
                }
              };
              return (
                <tr
                  key={rowKey}
                  role={clickable ? "button" : rowProps.role}
                  tabIndex={clickable ? 0 : rowProps.tabIndex}
                  aria-label={rowProps["aria-label"]}
                  aria-selected={rowProps["aria-selected"]}
                  onClick={clickable ? handleActivate : rowProps.onClick}
                  onKeyDown={handleKeyDown}
                  onMouseEnter={() => setHoveredRowKey(rowKey)}
                  onMouseLeave={() => setHoveredRowKey((current) => (current === rowKey ? null : current))}
                  style={{
                    background: hoveredRowKey === rowKey ? colors.tableRowHover : "transparent",
                    transition: "background-color 120ms ease",
                    ...rowProps.style,
                  }}
                  className={rowProps.className}
                >
                  {columns.map((column) => {
                    const columnId = resolveColumnId(column);
                    const align = column.align || "left";
                    return (
                      <td
                        key={columnId || align}
                        style={{
                          ...cellBaseStyle,
                          textAlign: align,
                          width: resolveColumnWidth(column),
                          minWidth: column.minWidth,
                          ...column.cellStyle,
                        }}
                      >
                        {renderCell(column, row, rowIndex)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columnCount} style={{ ...cellBaseStyle, ...emptyStateStyle }}>
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;

const resizeHandleStyle = {
  position: "absolute",
  top: 0,
  right: -3,
  width: 8,
  height: "100%",
  cursor: "col-resize",
  zIndex: 2,
};
